import './App.css'
import { RootStateBase } from './store/baseStore';
import Root from './view/layout/Root';
import { GridProvider } from './utils/context/gridContext';
import ToastPortal from './components/ToastPortal';

import GlobalStyle from "./styles/global";
import { useTheme } from './utils/context/themeContext';
import SideBarOptions from "../src/utils/SideBarOptions";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18next from "i18next";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Authentication from './view/authentication/authentication';
import AccessTokenAuth from './view/authentication/accessTokenAuth';
import MasterLoading from '../src/view/states/masterLoading/index';
import MasterError from './view/states/masterError';
import 'react-toastify/dist/ReactToastify.css';
import MasterSuccess from './view/states/masterSuccess';
import MasterPartial from './view/states/masterPartial';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom'
import { SetSistemaID } from './store/util/action/utilAction';
import { CheckHasLogin, ResetAuthState } from './store/authentication/action/authenticationAction';
import { useSearchParams } from 'react-router-dom';
import { CustomProvider, NormalizeURL, PathName, ReactRouteObject, RouteParamters } from './utils/routeUtil';
import { SidebarProvider } from './utils/context/sidebarContext';
import PageNotFound from './view/error/PageNotFound';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { IsDebug, IsStorybook } from 'domain/utils/domainUtil';
import ReduxView from './view/states/reduxView';
// import { StoreAplication } from './main';
// import { DispatchLoading, DispatchType } from 'domain/utils/ReactUtils';
// import { StateType } from 'domain/types/base/base';

const connector = connectUtil(
  (state: RootStateBase) => ({
    authIgnore: state.UtilReducer.AuthIgnore,
    authState: state.AuthenticationReducer.authState,
    startState: state.UtilReducer.StartState,
  }),
  { SetSistemaID, CheckHasLogin, ResetAuthState }
);


interface Props extends PropsFromRedux<typeof connector> {
  children?: JSX.Element | React.ReactNode,
  routes?: ReactRouteObject[]
  SidebarOptions?: Array<SideBarOptions>
  headerLeftInfo?: React.ReactNode | JSX.Element
  ignoreAuth?: boolean
  sistemaID?: number
  customWrapper?: React.ComponentType<CustomProvider>
  accessTokenRequiredClaims?: string[]
  accessTokenValidatorUrl?: string
}

i18next.init({
  interpolation: { escapeValue: false }, // React already does escaping
});

function App(props: Props) {
  const { toggleTheme } = useTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();

  const logOut = searchParams.get(RouteParamters.logOut);
  const accessToken = searchParams.get(RouteParamters.access_token);

  // comentei isso pq tava zuando com minhas rota de detalhe pagamento/:id ai a parada de baixo resolveu mas não sei se vai ferrrar com outra coisa
  // const validRoute = props.routes?.find((r: ReactRouteObject) => r.path == (location.pathname == "/" ? location.pathname : location.pathname.replace("/", "")))

  const validRoute = props.routes?.find((r: ReactRouteObject) =>
    r.path === location.pathname || location.pathname.startsWith(r.path || '')
  );

  useEffect(() => {
    const { fixedURL, hadExtraSlashes } = NormalizeURL(window.location.href);
    if (hadExtraSlashes) {
      window.history.replaceState(null, "", fixedURL);
    }

  }, [location])

  useEffect(() => {
    props.SetSistemaID(props.sistemaID)

    if (accessToken) {
      props.ResetAuthState()
    } else {
      props.CheckHasLogin(logOut != null)
    }

    const lang = searchParams.get(RouteParamters.lang);

    if (lang != null) {
      i18n.changeLanguage(lang);
    }
  }, [])


  const queryClient = new QueryClient();

  IsDebug()
  IsStorybook()

  const Core: JSX.Element = (
    <I18nextProvider i18n={i18next}>
      <GridProvider>
        <SidebarProvider>
          <ToastPortal />
          <GlobalStyle />
          <ReduxView />
          {
            validRoute == undefined ?
              <PageNotFound /> :
              props.startState && (
                ((props.authIgnore && props.authIgnore.includes(PathName(location.pathname))) || props.authState == true) ?
                  (
                    <Root headerInfo={props.headerLeftInfo} toggleTheme={toggleTheme} sideBarOptions={props.SidebarOptions} />
                  ) :
                  props.children ?
                    <>{props.children}</> :
                    (
                      (accessToken ? <AccessTokenAuth requiredClaims={props.accessTokenRequiredClaims} validatorUrl={props.accessTokenValidatorUrl} /> : <Authentication sistemaID={props.sistemaID} />)
                    )
              )
          }
          <MasterError />
          <MasterLoading />
          <MasterSuccess />
          <MasterPartial />
        </SidebarProvider>
      </GridProvider>
    </I18nextProvider>)


  return (
    <QueryClientProvider client={queryClient}>
      <CustomWrapper provider={props.customWrapper}>{Core}
      </CustomWrapper>
    </QueryClientProvider>
  )
}

function CustomWrapper({ provider: Provider, children }: { provider?: React.ElementType; children: React.ReactNode }) {
  return Provider ? <Provider>{children}</Provider> : <>{children}</>;
}

export default connector(App);
